// src/context/AuthContext.js
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import {
  BrowserAuthError,
  InteractionRequiredAuthError,
} from "@azure/msal-browser";
import { API_SCOPE } from "../auth/msal";

const API_BASE = process.env.REACT_APP_API_URL;

export const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  // --- MSAL wiring ---
  const { instance } = useMsal();
  const msalIsAuthenticated = useIsAuthenticated();

  // --- shared state ---
  const [authData, setAuthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSso, setIsSso] = useState(false);

  // Feature flag: force SSO if desired
  const forceSso =
    String(process.env.REACT_APP_USE_MSAL || "").toLowerCase() === "true";

  // Legacy refresh timer
  const refreshIntervalRef = useRef(null);

  // ===== MSAL / SSO helpers =====
  const getMsalAccount = () => instance.getAllAccounts()[0] || null;

  const ssoComputeAuthState = async () => {
    const account = getMsalAccount();
    if (!account) {
      setAuthData(null);
      return;
    }
    const claims = account.idTokenClaims || {};
    setAuthData({
      id: claims.oid || claims.sub,
      email: (claims.preferred_username || claims.email || "").toLowerCase(),
      name: claims.name,
      roles: claims.roles || [],
      provider: "sso",
    });
  };

  const ssoGetAccessToken = async (opts = {}) => {
    const account = getMsalAccount();
    if (!account) return null;

    try {
      // First try silent (no UX)
      const res = await instance.acquireTokenSilent({
        account,
        scopes: [API_SCOPE],
        ...opts,
      });
      return res.accessToken;
    } catch (err) {
      console.warn("acquireTokenSilent failed, falling back to interactive", err);

      // Interaction is required (expired, no consent, etc.)
      if (err instanceof InteractionRequiredAuthError) {
        try {
          // Try popup first
          const popupRes = await instance.acquireTokenPopup({
            account,
            scopes: [API_SCOPE],
            ...opts,
          });
          return popupRes.accessToken;
        } catch (popupErr) {
          console.error("acquireTokenPopup error", popupErr);

          // If popup is blocked, fall back to redirect
          if (
            popupErr instanceof BrowserAuthError &&
            popupErr.errorCode === "popup_window_error"
          ) {
            instance.acquireTokenRedirect({
              account,
              scopes: [API_SCOPE],
              ...opts,
            });
            // Redirect takes over; function effectively won't return in practice.
            return null;
          }

          throw popupErr;
        }
      }

      throw err;
    }
  };

  const loginSso = async (options = {}) => {
    setLoading(true);
    try {
      // Prefer popup UX
      await instance.loginPopup({
        scopes: [API_SCOPE],
        ...options,
      });

      await ssoComputeAuthState();
      setIsSso(true);
      stopLegacyRefreshLoop();
      return { success: true };
    } catch (e) {
      console.error("loginSso error", e);

      // If popup was blocked, fall back to redirect
      if (
        e instanceof BrowserAuthError &&
        e.errorCode === "popup_window_error"
      ) {
        instance.loginRedirect({
          scopes: [API_SCOPE],
          ...options,
        });
        // Redirect is in progress; this promise won't really finish
        return { success: false, redirect: true };
      }

      return { success: false, error: e?.message || String(e) };
    } finally {
      setLoading(false);
    }
  };

  const logoutSso = async () => {
    setLoading(true);
    try {
      const account = getMsalAccount();
      if (account) {
        // popup avoids full-page redirect
        await instance.logoutPopup({ account });
      }
      setAuthData(null);
    } finally {
      setLoading(false);
    }
  };

  // ===== Legacy (cookie/JWT) helpers =====
  const legacyCheckAuthStatus = async () => {
    try {
      const resp = await fetch(`${API_BASE}/api/auth-status/`, {
        method: "GET",
        credentials: "include",
      });
      if (!resp.ok) {
        setAuthData(null);
        return;
      }
      const data = await resp.json();
      setAuthData(
        data?.isAuthenticated ? { ...data.user, provider: "legacy" } : null
      );
    } catch {
      setAuthData(null);
    }
  };

  const loginLegacy = async ({ email, password }) => {
    setLoading(true);
    try {
      const resp = await fetch(`${API_BASE}/api/users/login/`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        return {
          success: false,
          error: err.detail || "Invalid email or password",
        };
      }
      await legacyCheckAuthStatus();
      setIsSso(false);
      startLegacyRefreshLoop();
      return { success: true };
    } catch {
      return {
        success: false,
        error: "An unexpected error occurred. Please try again.",
      };
    } finally {
      setLoading(false);
    }
  };

  const refreshLegacyToken = async () => {
    try {
      const resp = await fetch(`${API_BASE}/api/users/token/refresh/`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (resp.ok) {
        await legacyCheckAuthStatus();
      } else {
        setAuthData(null);
      }
    } catch {
      setAuthData(null);
    }
  };

  const startLegacyRefreshLoop = () => {
    if (refreshIntervalRef.current) return;
    refreshIntervalRef.current = setInterval(
      refreshLegacyToken,
      14 * 60 * 1000
    );
  };

  const stopLegacyRefreshLoop = () => {
    if (!refreshIntervalRef.current) return;
    clearInterval(refreshIntervalRef.current);
    refreshIntervalRef.current = null;
  };

  const logoutLegacy = async () => {
    setLoading(true);
    try {
      await fetch(`${API_BASE}/api/logout/`, {
        method: "POST",
        credentials: "include",
      });
      setAuthData(null);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  // ===== Mode detection =====
  const detectMode = async () => {
    const hasMsal = getMsalAccount() || msalIsAuthenticated || forceSso;
    if (hasMsal) {
      setIsSso(true);
      stopLegacyRefreshLoop();
      await ssoComputeAuthState();
    } else {
      setIsSso(false);
      await legacyCheckAuthStatus();
      startLegacyRefreshLoop();
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await detectMode();
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msalIsAuthenticated, forceSso]);

  useEffect(
    () => () => {
      stopLegacyRefreshLoop();
    },
    []
  );

  // ===== Public API =====

  // Back-compat: single login() used by your current Login component.
  // - Called with args → legacy
  // - Called with no args → SSO
  const login = async (credsOrNothing) => {
    if (credsOrNothing && typeof credsOrNothing === "object") {
      return loginLegacy(credsOrNothing);
    }
    return loginSso();
  };

  const logout = async () => (isSso ? logoutSso() : logoutLegacy());

  const getAccessToken = async () => (isSso ? ssoGetAccessToken() : null);

  // Unified fetch helper: sends Bearer for SSO, cookies for legacy
  const apiFetch = async (url, options = {}) => {
    const headers = { ...(options.headers || {}) };
    if (isSso) {
      const token = await getAccessToken();
      if (token) headers.Authorization = `Bearer ${token}`;
      return fetch(url, { ...options, headers, credentials: "omit" });
    }
    return fetch(url, { ...options, headers, credentials: "include" });
  };

  const value = useMemo(
    () => ({
      // state
      authData,
      loading,
      isSso,

      // logins
      login, // back-compat (no args = SSO; args = legacy)
      loginSso,
      loginLegacy,

      // logout
      logout,

      // tokens & fetch
      getAccessToken,
      apiFetch,
    }),
    [authData, loading, isSso]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
